import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowRight, Feather, Zap, Globe } from "lucide-react";
import { MaskLines, Reveal } from "../components/Reveal";
import { BrandMarquee } from "../components/BrandMarquee";
import { PRODUCT, IMAGES, MANIFESTO, CHECKOUT_URL } from "../lib/brand";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yProduct = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scaleProduct = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 120]);

  // mouse parallax / 3D tilt
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 15 });
  const tx = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="grain relative overflow-hidden bg-[#212121] pt-28 pb-16"
      data-testid="hero-section"
    >
      {/* background glows */}
      <motion.div style={{ y: yBg }} className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-10 h-[38rem] w-[38rem] rounded-full bg-[#184786]/40 blur-[130px]" />
        <div className="absolute -right-32 bottom-0 h-[34rem] w-[34rem] rounded-full bg-[#EE4026]/30 blur-[130px]" />
      </motion.div>

      <div className="relative z-10 mx-auto grid max-w-[1400px] items-center gap-6 px-5 sm:px-8 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <Reveal delay={0.15}>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70">
              <span className="h-1.5 w-1.5 rounded-full bg-[#EE4026]" /> The original kick toy
            </span>
          </Reveal>

          <h1 className="font-display mt-6 text-white" data-testid="hero-headline">
            <MaskLines
              className="block text-[13vw] leading-[0.95] uppercase sm:text-7xl sm:leading-[0.88] lg:text-8xl"
              lines={["Gravity is", <span key="o" className="text-[#EE4026]">optional.</span>, "Fun is"]}
              start={0.35}
            />
            <MaskLines
              className="block text-[13vw] leading-[0.95] uppercase sm:text-7xl sm:leading-[0.88] lg:text-8xl"
              lines={[<span key="m" className="text-[#184786] [-webkit-text-stroke:2px_#fff]">mandatory.</span>]}
              start={0.62}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-md text-base leading-relaxed text-white/70 sm:text-lg"
            data-testid="hero-subheading"
          >
            One weighted base, a burst of aerodynamic feathers, infinite rallies. Featherbomb turns any
            patch of ground into your court.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-button"
              className="btn-primary flex items-center gap-2 rounded-full px-7 py-4 text-sm font-bold uppercase tracking-wide"
            >
              Order Yours — ${PRODUCT.price} <ArrowRight size={18} />
            </a>
            <Link
              to="/about"
              className="link-underline flex items-center gap-2 py-4 text-sm font-bold uppercase tracking-wide text-white"
            >
              Our Story
            </Link>
          </motion.div>

        </div>

        {/* Floating product */}
        <div className="relative flex justify-center" style={{ perspective: 1000 }}>
          <motion.div style={{ y: yProduct, scale: scaleProduct }} className="relative">
            <motion.div style={{ rotateX: rx, rotateY: ry, x: tx }} className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.85, y: 40 }}
                animate={{ opacity: 1, scale: 1, y: [0, -18, 0] }}
                transition={{
                  opacity: { delay: 0.5, duration: 0.9 },
                  scale: { delay: 0.5, duration: 0.9 },
                  y: { delay: 1.4, duration: 5, repeat: Infinity, ease: "easeInOut" },
                }}
                className="relative z-10 mx-auto w-[82%] max-w-md overflow-hidden rounded-[2rem] border border-white/10 bg-[#F5F5F5] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)] sm:w-[74%]"
              >
                <img
                  src={IMAGES.productReal}
                  alt="Featherbomb kick toy"
                  data-testid="hero-product-preview"
                  className="w-full object-cover"
                />
                <div className="absolute left-4 top-4 rounded-full bg-[#212121] px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
                  Classic Edition
                </div>
                <div className="absolute bottom-4 right-4 rounded-full bg-[#EE4026] px-4 py-2 font-display text-lg leading-none text-white">
                  ${PRODUCT.price}
                </div>
              </motion.div>
              <div className="absolute inset-0 -z-0 mx-auto h-full w-[60%] rounded-full bg-[#EE4026]/25 blur-3xl" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const featureIcons = [Feather, Zap, Globe];

const Home = () => {
  return (
    <div data-testid="home-page">
      <Hero />
      <BrandMarquee dark />

      {/* What is it */}
      <section className="relative overflow-hidden bg-[#F5F5F5] px-5 py-24 sm:px-8 sm:py-32">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl bg-gradient-to-b from-white to-[#E9E9E9] shadow-2xl">
                <img
                  src={IMAGES.productReal}
                  alt="Featherbomb kick toy"
                  className="aspect-[4/5] w-full object-contain p-4"
                />
              </div>
              <div className="absolute -bottom-6 -right-4 rounded-2xl bg-[#EE4026] px-6 py-4 text-white shadow-xl sm:-right-6">
                <p className="font-display text-[2.15rem] leading-none">∞</p>
                <p className="font-mono text-[11.5px] uppercase tracking-widest">rallies</p>
              </div>
            </div>
          </Reveal>

          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#184786]">What is a Featherbomb?</p>
            <h2 className="font-display mt-4 text-4xl uppercase leading-[0.95] text-[#212121] sm:text-6xl">
              Kick it. Chase it.<br /><span className="text-[#EE4026]">Never let it land.</span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#212121]/70 sm:text-lg">
              Keep Featherbomb it in the air with your feet, knees, head... whatever it takes but just not
              your hands. Play solo, rally with a friend, or get a whole flock going. It's the easiest game
              you'll never want to stop playing.
            </p>

            <div className="mt-10 grid gap-5 sm:grid-cols-3">
              {PRODUCT.features.map((f, i) => {
                const Icon = featureIcons[i];
                return (
                  <Reveal key={f.title} delay={i * 0.1}>
                    <div className="group h-full rounded-2xl border border-black/10 bg-white p-5 transition hover:border-[#EE4026] hover:shadow-lg">
                      <Icon className="text-[#EE4026]" size={22} />
                      <p className="mt-4 font-bold uppercase tracking-wide text-[#212121]">{f.title}</p>
                      {f.subtitle && (
                        <p className="mt-1 text-sm font-semibold text-[#184786]">{f.subtitle}</p>
                      )}
                      <p className="mt-2 text-sm leading-relaxed text-[#212121]/60">{f.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto teaser */}
      <section className="bg-[#212121] px-5 py-24 sm:px-8 sm:py-32">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#EE4026]">The Manifesto</p>
              <h2 className="font-display mt-3 text-4xl uppercase leading-[0.95] text-white sm:text-6xl">
                Why we made it
              </h2>
            </div>
            <Link to="/about" className="link-underline flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white">
              Read the full story <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {MANIFESTO.slice(0, 2).map((m, i) => (
              <Reveal key={m.n} delay={i * 0.12}>
                <div className="h-full rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition hover:border-[#EE4026]/50">
                  <span className="font-display text-6xl text-[#EE4026]/80">{m.n}</span>
                  <h3 className="font-display mt-3 text-2xl uppercase text-white">{m.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">
                    {m.text.split("\n\n").slice(0, 2).join(" ").replace(/\*\*/g, "")}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="relative overflow-hidden bg-[#EE4026] px-5 py-24 sm:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <h2 className="font-display text-4xl uppercase leading-[0.95] text-white sm:text-7xl">
              Ready to keep it up?
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-white/85 sm:text-lg">
              Free local pick up in the lower mainland of BC, fast shipping in the rest of Canada. Hang tight
              as we lock down logistics for the rest of the globe!
            </p>
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-wide text-[#EE4026] transition hover:-translate-y-1 hover:shadow-2xl"
            >
              Order Your Featherbomb <ArrowRight size={18} />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
